import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ChildProvider } from './contexts/ChildContext';
import DashboardLayout from './components/DashboardLayout';
import Login from './pages/Login';
import Register from './pages/Register';
import AuthCallback from './pages/AuthCallback';
import ErisimYok from './pages/ErisimYok';
import DenemeSinaviVideolari from './pages/public/DenemeSinaviVideolari';
import DenemeSinaviBook from './pages/public/DenemeSinaviBook';

// Teacher pages
import TeacherDashboard from './pages/teacher/Dashboard';
import TeacherClasses from './pages/teacher/Classes';
import TeacherLibrary from './pages/teacher/Library';
import TeacherAssignments from './pages/teacher/Assignments';
import TeacherAssignmentPerformance from './pages/teacher/AssignmentPerformance';
import TeacherAppointments from './pages/teacher/Appointments';
import TeacherLibraryDuties from './pages/teacher/LibraryDuties';
import TeacherDigikoc from './pages/teacher/Digikoc';
import TeacherSchedule from './pages/teacher/Schedule';
import TeacherPerformance from './pages/teacher/Performance';
import TeacherHelp from './pages/teacher/Help';
import TeacherInstitutions from './pages/teacher/Institutions';
import TeacherSmartBoard from './pages/teacher/SmartBoard';
import TeacherProfile from './pages/teacher/Profile';

// Student pages
import StudentDashboard from './pages/student/Dashboard';
import StudentClasses from './pages/student/Classes';
import StudentInstitutions from './pages/student/Institutions';
import StudentLibrary from './pages/student/Library';
import StudentAssignments from './pages/student/Assignments';
import StudentAppointments from './pages/student/Appointments';
import StudentSmartBoard from './pages/student/SmartBoard';
import StudentSchedule from './pages/student/Schedule';
import StudentDigikoc from './pages/student/Digikoc';
import StudentBookContent from './pages/student/BookContent';
import StudentHelp from './pages/student/Help';
import StudentProfile from './pages/student/Profile';
import StudentPerformance from './pages/student/Performance';
import StudentDailyData from './pages/student/DailyData';
import StudentReports from './pages/student/Reports';
import StudentExamAppointments from './pages/student/ExamAppointments';
import StudentLibraryReservations from './pages/student/LibraryReservations';

// Parent pages
import ParentDashboard from './pages/parent/Dashboard';
import ParentPerformance from './pages/parent/Performance';
import ParentAppointments from './pages/parent/Appointments';
import ParentStudentAssignments from './pages/parent/StudentAssignments';
import ParentHelp from './pages/parent/Help';
import ParentStudentSelection from './pages/parent/StudentSelection';
import ParentProfile from './pages/parent/Profile';
import ParentProgram from './pages/parent/Program';

const RootRedirect: React.FC = () => {
  const { user } = useAuth();
  if (user) return <Navigate to={`/${user.role}`} replace />;
  return <Navigate to="/login" replace />;
};

function App() {
  return (
    <AuthProvider>
      <ChildProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/erisim-yok" element={<ErisimYok />} />

          {/* Public "Deneme Sınavı Videoları" — no login required */}
          <Route path="/deneme-sinavi-videolari" element={<DenemeSinaviVideolari />} />
          <Route path="/deneme-sinavi-videolari/:bookId" element={<DenemeSinaviBook />} />

          {/* Teacher Routes */}
          <Route path="/teacher" element={<DashboardLayout requiredRole="teacher" />}>
            <Route index element={<TeacherDashboard />} />
            <Route path="siniflarim" element={<TeacherClasses />} />
            <Route path="kurumlarim" element={<TeacherInstitutions />} />
            <Route path="kutuphanem" element={<TeacherLibrary />} />
            <Route path="odevlerim" element={<TeacherAssignments />} />
            <Route path="odevlerim/performans" element={<TeacherAssignmentPerformance />} />
            <Route path="odevlerim/:id" element={<TeacherAssignments />} />
            <Route path="randevularim" element={<TeacherAppointments />} />
            <Route path="kutuphane-saati" element={<TeacherLibraryDuties />} />
            <Route path="digikoc" element={<TeacherDigikoc />} />
            <Route path="programim" element={<TeacherSchedule />} />
            <Route path="akilli-tahta" element={<TeacherSmartBoard />} />
            <Route path="yardim" element={<TeacherHelp />} />
            <Route path="profil" element={<TeacherProfile />} />
          </Route>

          {/* Student Routes */}
          <Route path="/student" element={<DashboardLayout requiredRole="student" />}>
            <Route index element={<StudentDashboard />} />
            <Route path="siniflarim" element={<StudentClasses />} />
            <Route path="kurumlarim" element={<StudentInstitutions />} />
            <Route path="kutuphanem" element={<StudentLibrary />} />
            <Route path="kitap-icerik" element={<StudentBookContent />} />
            <Route path="odevler" element={<StudentAssignments />} />
            <Route path="randevularim" element={<StudentAppointments />} />
            <Route path="kutuphane-rezervasyon" element={<StudentLibraryReservations />} />
            <Route path="deneme-sinavlari" element={<StudentExamAppointments />} />
            <Route path="akilli-tahta" element={<StudentSmartBoard />} />
            <Route path="ogrenci-programi" element={<StudentSchedule />} />
            <Route path="digikoc" element={<StudentDigikoc />} />
            <Route path="performansim" element={<StudentPerformance />} />
            <Route path="gunluk-verilerim" element={<Navigate to="/student/performansim?tab=gunluk" replace />} />
            <Route path="raporlarim" element={<Navigate to="/student/performansim?tab=raporlar" replace />} />
            <Route path="yardim" element={<StudentHelp />} />
            <Route path="profil" element={<StudentProfile />} />
          </Route>

          {/* Parent Routes */}
          <Route path="/parent" element={<DashboardLayout requiredRole="parent" />}>
            <Route index element={<ParentDashboard />} />
            <Route path="ogrenci-secimi" element={<ParentStudentSelection />} />
            <Route path="performans" element={<ParentPerformance />} />
            <Route path="ogrenci-programi" element={<ParentProgram />} />
            <Route path="randevularim" element={<ParentAppointments />} />
            <Route path="ogrenci-odevleri" element={<ParentStudentAssignments />} />
            <Route path="yardim" element={<ParentHelp />} />
            <Route path="profil" element={<ParentProfile />} />
          </Route>

          {/* Catch all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      </ChildProvider>
    </AuthProvider>
  );
}

export default App;
